import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  deleteDoc,
  getError,
  httpsCallable,
  isFailedPreconditionError,
  serialise,
  snapshot,
  type SerialisedData,
  unserialise$,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { AppointmentRequestActions, SchedulingActions } from '../actions';
import { cancelAction } from '@principle-theorem/ng-shared';
import {
  DepositStatus,
  isDepositAuthorisationLapsedError,
  type IApproveBookingRequestDepositRequest,
  type IAppointmentRequest,
  type IRefundDepositRequest,
  type IRefundDepositResponse,
} from '@principle-theorem/principle-core/interfaces';

@Injectable()
export class AppointmentRequestEffects {
  private _actions$ = inject(Actions);
  private _snackBar = inject(MatSnackBar);
  completeRequest$ = createEffect(() => this._completeRequest$());
  cancelRequest$ = createEffect(() => this._cancelAppointmentRequest$(), {
    dispatch: false,
  });

  /**
   * Names what happened to the patient's money, because "rejected" alone
   * leaves staff unsure whether a hold was released or a payment refunded.
   */
  private static _rejectedMessage(
    appointmentRequest: WithRef<IAppointmentRequest>
  ): string {
    switch (appointmentRequest.deposit?.metadata?.status) {
      case DepositStatus.Authorised:
        return 'Booking request rejected. The card hold has been released.';
      case DepositStatus.Paid:
        return 'Booking request rejected. The deposit has been refunded.';
      default:
        return 'Booking request rejected.';
    }
  }

  private _completeRequest$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(SchedulingActions.saveNewAppointmentSuccess),
      unserialise$(),
      concatMap(async ({ appointment, appointmentRequest }) => {
        if (!appointmentRequest) {
          return cancelAction();
        }
        if (appointmentRequest.deposit?.requestRef) {
          try {
            await snapshot(
              httpsCallable<
                SerialisedData<IApproveBookingRequestDepositRequest>,
                void
              >('http-stripe-approveBookingRequestDeposit')(
                serialise({
                  appointmentRef: appointment.ref,
                  appointmentRequestRef: appointmentRequest.ref,
                })
              )
            );
          } catch (error) {
            if (isDepositAuthorisationLapsedError(error)) {
              this._snackBar.open(
                'Deposit authorisation has lapsed. Send a new payment link before approving this request.',
                'OK',
                { duration: 5000 }
              );
              return cancelAction();
            }
            if (!isFailedPreconditionError(error)) {
              throw error;
            }
            this._snackBar.open(error.message, 'OK', { duration: 5000 });
            return cancelAction();
          }
          return AppointmentRequestActions.clearAppointmentRequest();
        }
        await deleteDoc(appointmentRequest.ref);
        return AppointmentRequestActions.clearAppointmentRequest();
      })
    );
  }

  private _cancelAppointmentRequest$(): Observable<void> {
    return this._actions$.pipe(
      ofType(AppointmentRequestActions.cancelAppointmentRequest),
      unserialise$(),
      concatMap(async ({ appointmentRequest }) => {
        // Releasing a hold is a cold Stripe round trip, so the row can sit
        // there for seconds looking untouched. Say the work has started, or
        // staff reject the same request again thinking nothing happened.
        const pendingNotice = this._snackBar.open(
          'Rejecting booking request...'
        );
        try {
          await this._handleRefundIfNeeded(appointmentRequest);
          await deleteDoc(appointmentRequest.ref);
        } catch (error) {
          pendingNotice.dismiss();
          this._snackBar.open(
            getError(error, 'Failed to process deposit refund'),
            'OK',
            { duration: 5000 }
          );
          return;
        }
        pendingNotice.dismiss();
        this._snackBar.open(
          AppointmentRequestEffects._rejectedMessage(appointmentRequest),
          'OK',
          { duration: 5000 }
        );
      })
    );
  }

  private async _handleRefundIfNeeded(
    appointmentRequest: WithRef<IAppointmentRequest>
  ): Promise<void> {
    const deposit = appointmentRequest.deposit?.metadata;
    if (
      !deposit ||
      (deposit.status !== DepositStatus.Paid &&
        deposit.status !== DepositStatus.Authorised)
    ) {
      return;
    }

    if (!deposit.paymentAttemptRef) {
      throw new Error('Deposit marked as paid but missing payment reference');
    }

    const refundRequest: IRefundDepositRequest = {
      paymentAttemptRef: deposit.paymentAttemptRef,
      reason: 'Appointment request rejected by staff',
    };

    await snapshot(
      httpsCallable<
        SerialisedData<IRefundDepositRequest>,
        IRefundDepositResponse
      >('http-stripe-refundDeposit')(serialise(refundRequest))
    );
  }
}
